/* Workspace components: slider, hotspots, video toggle, sticky add-to-cart and button price sync. */

if (!customElements.get('workspace-slider')) {
  customElements.define(
    'workspace-slider',
    class WorkspaceSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.onScroll = () => window.requestAnimationFrame(() => this.update());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      step() {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      go(direction) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: direction * this.step(), behavior: reduce ? 'auto' : 'smooth' });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const max = Math.max(scrollWidth - clientWidth, 0);
        const isStatic = max < 2;
        this.toggleAttribute('data-static', isStatic);
        const size = Math.min(clientWidth / scrollWidth, 1);
        const ratio = max ? Math.min(Math.max(scrollLeft / max, 0), 1) : 0;
        this.style.setProperty('--ws-progress-size', `${(size * 100).toFixed(2)}%`);
        this.style.setProperty('--ws-progress-offset', `${(ratio * (1 / size - 1) * 100).toFixed(2)}%`);
        if (this.prev) this.prev.disabled = scrollLeft <= 2;
        if (this.next) this.next.disabled = scrollLeft >= max - 2;
      }
    }
  );
}

if (!customElements.get('workspace-hotspots')) {
  customElements.define(
    'workspace-hotspots',
    class WorkspaceHotspots extends HTMLElement {
      connectedCallback() {
        this.dots = Array.from(this.querySelectorAll('[data-hotspot]'));
        this.panels = Array.from(this.querySelectorAll('[data-hotspot-panel]'));
        if (!this.panels.length) return;
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.select(Number(dot.dataset.hotspot))));
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.select(this.index - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.select(this.index + 1));
        this.select(0, false);
      }

      select(index, focus = false) {
        const count = this.panels.length;
        this.index = ((index % count) + count) % count;
        this.panels.forEach((panel, i) => (panel.hidden = i !== this.index));
        this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', i === this.index ? 'true' : 'false'));
        if (focus) this.panels[this.index].focus();
      }
    }
  );
}

if (!customElements.get('workspace-video')) {
  customElements.define(
    'workspace-video',
    class WorkspaceVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.button = this.querySelector('[data-video-toggle]');
        if (!this.video) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.video.removeAttribute('autoplay');
          this.video.pause();
        }
        this.button?.addEventListener('click', () => (this.video.paused ? this.video.play() : this.video.pause()));
        ['play', 'pause'].forEach((type) => this.video.addEventListener(type, () => this.sync()));
        this.sync();
      }

      sync() {
        if (!this.button) return;
        const paused = this.video.paused;
        this.button.setAttribute('aria-label', paused ? this.button.dataset.play : this.button.dataset.pause);
        this.button.toggleAttribute('data-paused', paused);
      }
    }
  );
}

if (!customElements.get('workspace-sticky-atc')) {
  customElements.define(
    'workspace-sticky-atc',
    class WorkspaceStickyAtc extends HTMLElement {
      connectedCallback() {
        this.productInfo = document.querySelector('product-info[data-section]');
        this.mainButton = this.productInfo?.querySelector('.product-form__submit');
        this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        this.button = this.querySelector('[data-sticky-submit]');
        if (!this.mainButton || !this.form || !this.button) return;

        this.button.addEventListener('click', () => {
          if (this.mainButton.disabled) return;
          this.mainButton.click();
        });

        this.sync();
        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });

        this.footer = document.querySelector('.shopify-section-group-footer-group');
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.ticking = false;
            const mainRect = this.mainButton.getBoundingClientRect();
            const passedMain = mainRect.bottom < 0;
            const mobile = window.matchMedia('(max-width: 749px)').matches;
            const belowViewport = mainRect.top >= window.innerHeight;
            const footerInView = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
            this.toggleAttribute('data-visible', (passedMain || (mobile && belowViewport)) && !footerInView);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      sync() {
        const label = this.mainButton.querySelector('span:not(.ws-btn-price)')?.textContent.trim();
        const price = this.mainButton.querySelector('.ws-btn-price')?.textContent.trim();
        this.button.disabled = this.mainButton.disabled;
        this.button.querySelector('[data-sticky-label]').textContent = label || '';
        const priceTarget = this.button.querySelector('[data-sticky-price]');
        priceTarget.textContent = this.mainButton.disabled ? '' : price || '';
        const productPrice = this.productInfo.querySelector('[id^="price-"] .price-item--last, [id^="price-"] .price-item--regular');
        const priceText = this.querySelector('[data-sticky-product-price]');
        if (priceText && productPrice) priceText.textContent = productPrice.textContent.trim();
        const variantInput = this.form.querySelector('input[name="id"]');
        const variantName = this.querySelector('[data-sticky-variant]');
        if (variantName && variantInput) {
          const selected = Array.from(this.productInfo.querySelectorAll('variant-selects input:checked, variant-selects select'))
            .map((element) => (element.tagName === 'SELECT' ? element.selectedOptions[0]?.textContent.trim() : element.value))
            .filter(Boolean)
            .join(' / ');
          if (selected) variantName.textContent = selected;
        }
      }
    }
  );
}

/* Keep the price inside the main add-to-cart button in step with the selected variant. */
(() => {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const next = data.html?.getElementById(`ProductSubmitButton-${data.sectionId}`)?.querySelector('.ws-btn-price');
    document.querySelectorAll(`[id^="ProductSubmitButton-"]`).forEach((button) => {
      if (!button.closest(`product-info[data-section="${data.sectionId}"]`)) return;
      const current = button.querySelector('.ws-btn-price');
      if (current && next) current.textContent = next.textContent;
    });
  });
})();

/* Product details section: re-render on variant change when it reads variant fields. */
if (!customElements.get('workspace-specs')) {
  customElements.define(
    'workspace-specs',
    class WorkspaceSpecs extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-variant-aware') || typeof subscribe !== 'function') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data.variant?.id) return;
          const url = `${window.location.pathname}?variant=${data.variant.id}&section_id=${this.dataset.section}`;
          fetch(url)
            .then((response) => response.text())
            .then((text) => {
              const next = new DOMParser().parseFromString(text, 'text/html').querySelector('workspace-specs');
              if (next) this.innerHTML = next.innerHTML;
            })
            .catch(() => {});
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }
    }
  );
}

/* Section menu: sticky in-page navigation built from sections that declare data-ws-anchor. */
if (!customElements.get('workspace-jump-nav')) {
  customElements.define(
    'workspace-jump-nav',
    class WorkspaceJumpNav extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.ws-jump__list');
        if (!this.list) return;
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.build();
        // Some sections (e.g. product recommendations) render after page load.
        this.observer = new MutationObserver(() => {
          clearTimeout(this.rebuildTimer);
          this.rebuildTimer = setTimeout(() => this.build(), 250);
        });
        this.observer.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
      }

      build() {
        const sections = Array.from(document.querySelectorAll('[data-ws-anchor]')).filter((el) => el.dataset.wsAnchor.trim());
        const signature = sections.map((el) => el.dataset.wsAnchor.trim()).join('|');
        if (signature === this.signature) return;
        this.signature = signature;
        this.list.replaceChildren();
        this.targets = [];
        if (sections.length < 2) {
          this.hidden = true;
          return;
        }
        const first = this.closest('.shopify-section')?.previousElementSibling || document.getElementById('MainContent');
        const add = (label, target) => {
          if (!target.id) target.id = `ws-anchor-${this.targets.length}`;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${target.id}`;
          a.textContent = label;
          a.addEventListener('click', (event) => {
            event.preventDefault();
            const top = target.getBoundingClientRect().top + window.scrollY - this.offset() + 1;
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
            history.replaceState(null, '', a.hash);
          });
          li.appendChild(a);
          this.list.appendChild(li);
          this.targets.push({ link: a, target });
        };
        if (first) add((this.list.dataset.overviewLabel || 'Overview').trim(), first);
        sections.forEach((el) => add(el.dataset.wsAnchor.trim(), el.closest('.shopify-section') || el));
        this.hidden = false;
        this.style.setProperty('--ws-jump-h', `${this.querySelector('.ws-jump__bar').offsetHeight}px`);
        this.lastCurrent = null;
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.observer?.disconnect();
      }

      headerBottom() {
        const header = document.querySelector('.section-header');
        if (!header) return 0;
        return Math.max(header.getBoundingClientRect().bottom, 0);
      }

      offset() {
        return this.headerBottom() + this.querySelector('.ws-jump__bar').offsetHeight;
      }

      update() {
        if (!this.targets?.length) return;
        const top = this.headerBottom();
        this.style.setProperty('--ws-jump-top', `${top}px`);
        this.toggleAttribute('data-stuck', this.getBoundingClientRect().top < top);
        const line = this.offset() + 8;
        let current = this.targets[0];
        this.targets.forEach((item) => {
          if (item.target.getBoundingClientRect().top <= line) current = item;
        });
        this.targets.forEach((item) => item.link.setAttribute('aria-current', item === current ? 'true' : 'false'));
        if (current && this.lastCurrent !== current) {
          this.lastCurrent = current;
          const list = this.list;
          const left = current.link.offsetLeft - list.clientWidth / 2 + current.link.offsetWidth / 2;
          list.scrollTo({ left, behavior: 'auto' });
        }
      }
    }
  );
}

/* Tabs (product slider): roving tabindex, arrow keys, one visible panel. */
if (!customElements.get('workspace-tabs')) {
  customElements.define(
    'workspace-tabs',
    class WorkspaceTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => {
            const keys = { ArrowRight: 1, ArrowLeft: -1 };
            if (keys[event.key] === undefined && event.key !== 'Home' && event.key !== 'End') return;
            event.preventDefault();
            let next = index + (keys[event.key] || 0);
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.tabs.length - 1;
            next = (next + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
          if (selected) panel?.querySelector('workspace-slider')?.update?.();
        });
      }
    }
  );
}

/* Desktop mega menu: open on hover with intent delay (pointer devices only); click and keyboard keep working. */
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const init = () => {
    document.querySelectorAll('.header__inline-menu details.mega-menu, .header__inline-menu details[id^="Details-HeaderMenu"]').forEach((details) => {
      if (details.dataset.wsHover) return;
      details.dataset.wsHover = 'true';
      const item = details.closest('li') || details;
      let openTimer;
      let closeTimer;
      const closeOthers = () => {
        document.querySelectorAll('.header__inline-menu details[open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      };
      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => {
          closeOthers();
          details.setAttribute('open', '');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
        }, 120);
      });
      const panel = details.querySelector('.mega-menu__content, .header__submenu');
      const leave = (event) => {
        clearTimeout(openTimer);
        if (event.relatedTarget && (item.contains(event.relatedTarget) || panel?.contains(event.relatedTarget))) return;
        closeTimer = setTimeout(() => {
          details.removeAttribute('open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }, 220);
      };
      item.addEventListener('mouseleave', leave);
      panel?.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel?.addEventListener('mouseleave', leave);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Collection browsing: grid density toggle, Load more, quick filter chips (AJAX through Dawn's facets when present). */
(() => {
  const STORAGE_KEY = 'ws-grid-density';
  const readDensity = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'large';
    } catch (e) {
      return 'large';
    }
  };
  const applyDensity = (value) => {
    const grid = document.getElementById('product-grid');
    if (grid) grid.dataset.density = value;
    document.querySelectorAll('.ws-density__btn').forEach((btn) => btn.setAttribute('aria-pressed', btn.dataset.density === value ? 'true' : 'false'));
  };
  const init = () => applyDensity(readDensity());

  document.addEventListener('click', async (event) => {
    const densityBtn = event.target.closest('.ws-density__btn');
    if (densityBtn) {
      try {
        localStorage.setItem(STORAGE_KEY, densityBtn.dataset.density);
      } catch (e) {}
      applyDensity(densityBtn.dataset.density);
      return;
    }

    const chip = event.target.closest('[data-ws-filter-link]');
    if (chip && window.FacetFiltersForm && typeof FacetFiltersForm.renderPage === 'function') {
      event.preventDefault();
      FacetFiltersForm.renderPage(new URL(chip.href, window.location.origin).searchParams.toString());
      return;
    }

    const loadMore = event.target.closest('[data-load-more]');
    if (loadMore) {
      event.preventDefault();
      if (loadMore.getAttribute('aria-busy') === 'true') return;
      loadMore.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(loadMore.href);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = document.getElementById('product-grid');
        const nextGrid = doc.getElementById('product-grid');
        if (!grid || !nextGrid) throw new Error('grid missing');
        const firstNew = nextGrid.querySelector('.ws-grid__item:not(.ws-promo)');
        nextGrid.querySelectorAll(':scope > .ws-grid__item:not(.ws-promo)').forEach((item) => grid.appendChild(item));
        const current = loadMore.closest('.ws-load-more');
        const next = doc.querySelector('.ws-load-more');
        if (next) current.replaceWith(next);
        else current.remove();
        history.replaceState(null, '', loadMore.href);
        const link = firstNew && grid.querySelector(`a[href="${firstNew.querySelector('a')?.getAttribute('href')}"]`);
        link?.focus({ preventScroll: true });
      } catch (error) {
        window.location.href = loadMore.href;
      }
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  const container = document.getElementById('ProductGridContainer');
  if (container) new MutationObserver(init).observe(container, { childList: true });
})();

if (!customElements.get('workspace-reviews')) {
  customElements.define(
    'workspace-reviews',
    class WorkspaceReviews extends HTMLElement {
      connectedCallback() {
        this.step = Number(this.dataset.step) || 4;
        this.more = this.querySelector('[data-reviews-more]');
        this.more?.addEventListener('click', () => this.showMore());

        this.photos = Array.from(this.querySelectorAll('[data-review-photo]'));
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || !this.photos.length) return;
        this.image = this.dialog.querySelector('img');
        this.count = this.dialog.querySelector('.ws-reviews__dialog-count');
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-photo-index]');
          if (button) this.open(Number(button.dataset.photoIndex), button);
        });
        this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
        this.dialog.querySelector('[data-prev]').addEventListener('click', () => this.show(this.index - 1));
        this.dialog.querySelector('[data-next]').addEventListener('click', () => this.show(this.index + 1));
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.show(this.index - 1);
          if (event.key === 'ArrowRight') this.show(this.index + 1);
        });
        this.dialog.addEventListener('close', () => this.opener?.focus());
        this.dialog.querySelectorAll('[data-prev], [data-next]').forEach((button) => (button.hidden = this.photos.length < 2));
      }

      showMore() {
        const hidden = Array.from(this.querySelectorAll('.ws-review[hidden]'));
        const next = hidden.slice(0, this.step);
        next.forEach((review) => (review.hidden = false));
        next[0]?.focus();
        if (hidden.length <= this.step) this.more.hidden = true;
      }

      open(index, opener) {
        this.opener = opener;
        this.show(index);
        this.dialog.showModal();
      }

      show(index) {
        const total = this.photos.length;
        this.index = (index + total) % total;
        this.image.src = this.photos[this.index].dataset.full;
        this.count.textContent = `${this.index + 1} / ${total}`;
      }
    }
  );
}

